
import React, { useState } from 'react';

const ProjectManagement: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);

  const projects = [
    {
      id: 1,
      name: 'NFT数字藏品平台',
      client: '艺术科技公司',
      type: '区块链开发',
      status: '进行中',
      progress: 75,
      startDate: '2023-12-01',
      deadline: '2024-02-15',
      budget: '¥180,000',
      team: ['张三', '李四', '王五'],
      priority: 'high'
    },
    {
      id: 2,
      name: 'AI智能后台管理系统',
      client: '数据分析公司',
      type: 'AI系统开发',
      status: '测试中',
      progress: 90,
      startDate: '2023-11-15',
      deadline: '2024-01-30',
      budget: '¥220,000',
      team: ['赵六', '钱七'],
      priority: 'high'
    },
    {
      id: 3,
      name: '电商小程序开发',
      client: '零售连锁企业',
      type: '小程序开发',
      status: '开发中',
      progress: 60,
      startDate: '2024-01-01',
      deadline: '2024-03-01',
      budget: '¥95,000',
      team: ['孙八', '周九'],
      priority: 'medium'
    },
    {
      id: 4,
      name: 'PHP到Go迁移',
      client: '金融科技公司',
      type: '技术迁移',
      status: '规划中',
      progress: 25,
      startDate: '2024-01-10',
      deadline: '2024-04-15',
      budget: '¥150,000',
      team: ['吴十'],
      priority: 'medium'
    },
    {
      id: 5,
      name: 'DeFi流动性挖矿平台',
      client: '区块链投资公司',
      type: '区块链开发',
      status: '已完成',
      progress: 100,
      startDate: '2023-09-01',
      deadline: '2023-12-31',
      budget: '¥300,000',
      team: ['张三', '李四', '王五', '赵六'],
      priority: 'high'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case '进行中':
        return 'bg-blue-100 text-blue-800';
      case '测试中':
        return 'bg-yellow-100 text-yellow-800';
      case '开发中':
        return 'bg-green-100 text-green-800';
      case '规划中':
        return 'bg-gray-100 text-gray-800';
      case '已完成':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const filteredProjects = projects.filter(project => {
    if (activeFilter === 'all') return true;
    return project.status === activeFilter;
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">项目管理</h1>
          <p className="text-gray-600 mt-1">管理和跟踪所有项目进展</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          新建项目
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: '全部项目' },
            { key: '规划中', label: '规划中' },
            { key: '开发中', label: '开发中' },
            { key: '进行中', label: '进行中' },
            { key: '测试中', label: '测试中' },
            { key: '已完成', label: '已完成' }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeFilter === filter.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <div key={project.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Project Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{project.name}</h3>
                <p className="text-sm text-gray-600">{project.client}</p>
              </div>
              <div className="flex items-center space-x-2">
                <i className={`ri-flag-line ${getPriorityColor(project.priority)}`}></i>
                <button className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-more-line"></i>
                </button>
              </div>
            </div>

            {/* Project Info */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">类型</span>
                <span className="text-sm font-medium text-gray-900">{project.type}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">状态</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">预算</span>
                <span className="text-sm font-medium text-gray-900">{project.budget}</span>
              </div>
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">进度</span>
                <span className="text-sm font-medium text-gray-900">{project.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${project.progress}%` }}
                ></div>
              </div>
            </div>

            {/* Team */}
            <div className="mb-4">
              <span className="text-sm text-gray-600 block mb-2">团队成员</span>
              <div className="flex -space-x-2">
                {project.team.map((member, index) => (
                  <div
                    key={index}
                    className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-medium border-2 border-white"
                    title={member}
                  >
                    {member.charAt(0)}
                  </div>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="text-xs text-gray-500 space-y-1">
              <div>开始: {project.startDate}</div>
              <div>截止: {project.deadline}</div>
            </div>

            {/* Actions */}
            <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-200">
              <button className="flex-1 px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer whitespace-nowrap">
                查看详情
              </button>
              <button className="flex-1 px-3 py-2 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                编辑
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">新建项目</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">项目名称</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="请输入项目名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">客户名称</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="请输入客户名称"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">项目类型</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8">
                  <option>网站开发</option>
                  <option>小程序开发</option>
                  <option>APP开发</option>
                  <option>区块链开发</option>
                  <option>AI系统开发</option>
                  <option>技术迁移</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">项目预算</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="请输入项目预算"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                创建项目
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
